import React from 'react';
import {Button, Tooltip} from 'antd';
import {DeleteOutlined, EllipsisOutlined} from '@ant-design/icons';
import {FormattedMessage, useIntl} from 'react-intl';
import {StatusResponse} from '../../api-client';
import {buildQuickStatuses} from '../../utils/conditionShortcuts';

interface QuickStatusPaletteProps {
    /** Full list of loaded condition definitions; shortcuts resolve against this. */
    conditionDefinitions: StatusResponse[];
    /** Apply / select the chosen condition definition. */
    onSelect: (conditionDefinition: StatusResponse) => void;
    /** Highlight the button whose condition matches the tooth's / form's current one. */
    activeConditionId?: string | null;
    /** Optional "more" affordance — opens the full modal (floating palette / touch). */
    onMore?: () => void;
    /** Whether to render the "more" button. */
    showMore?: boolean;
    /** Clear/remove the tooth's current status (floating palette only). */
    onClear?: () => void;
    /** Whether to render the clear button (only when the tooth already has a status). */
    showClear?: boolean;
    /** Disables the clear button independently (e.g. delete in flight / no deletable id). */
    clearDisabled?: boolean;
    /** Disables all apply buttons while a quick-save is in flight. */
    saving?: boolean;
    className?: string;
}

/**
 * Compact row of the nine fixed condition shortcuts (C P G R O E K X F), shared
 * by the floating odontogram palette and the full condition modal. The full list
 * is ALWAYS rendered in canonical order; a shortcut that cannot be resolved to a
 * backend condition renders disabled (tooltip "Status is not configured") rather
 * than disappearing.
 */
const QuickStatusPalette: React.FC<QuickStatusPaletteProps> = ({
                                                                   conditionDefinitions,
                                                                   onSelect,
                                                                   activeConditionId,
                                                                   onMore,
                                                                   showMore,
                                                                   onClear,
                                                                   showClear,
                                                                   clearDisabled,
                                                                   saving,
                                                                   className,
                                                               }) => {
    const intl = useIntl();
    const shortcuts = buildQuickStatuses(conditionDefinitions);
    const notConfigured = intl.formatMessage({id: 'quick_status_not_configured'});

    return (
        <div
            className={'quick-status-palette' + (className ? ' ' + className : '')}
            role="group"
            // Prevent a click on the palette chrome from bubbling to the tooth
            // button underneath (which would open the full modal).
            onClick={event => event.stopPropagation()}
        >
            {shortcuts.map(({key, conditionDefinition}) => {
                const resolved = !!conditionDefinition;
                const label = resolved
                    ? (conditionDefinition!.displayName ?? conditionDefinition!.name ?? conditionDefinition!.nameEn ?? key)
                    : notConfigured;
                const active = resolved && !!activeConditionId && conditionDefinition!.id === activeConditionId;
                return (
                    <Tooltip key={key} title={label} placement="bottom" mouseEnterDelay={0.2}>
                        {/* span wrapper keeps the tooltip working on a disabled button */}
                        <span className="quick-status-btn-wrap">
                            <Button
                                className="quick-status-btn"
                                size="small"
                                type={active ? 'primary' : 'default'}
                                disabled={!resolved || saving}
                                aria-label={`${key} – ${label}`}
                                aria-pressed={active}
                                onClick={event => {
                                    event.stopPropagation();
                                    if (resolved) {
                                        onSelect(conditionDefinition!);
                                    }
                                }}
                            >
                                {key}
                            </Button>
                        </span>
                    </Tooltip>
                );
            })}
            {showMore && onMore && (
                <Tooltip title={<FormattedMessage id="quick_status_more"/>} placement="bottom" mouseEnterDelay={0.2}>
                    <Button
                        className="quick-status-btn quick-status-more"
                        size="small"
                        type="default"
                        aria-label="more"
                        onClick={event => {
                            event.stopPropagation();
                            onMore();
                        }}
                    >
                        <EllipsisOutlined/>
                    </Button>
                </Tooltip>
            )}
            {showClear && onClear && (
                <Tooltip title={<FormattedMessage id="quick_status_clear"/>} placement="bottom" mouseEnterDelay={0.2}>
                    <span className="quick-status-btn-wrap">
                        <Button
                            className="quick-status-btn quick-status-clear"
                            size="small"
                            type="default"
                            danger
                            disabled={saving || clearDisabled}
                            aria-label="clear status"
                            onClick={event => {
                                event.stopPropagation();
                                onClear();
                            }}
                        >
                            <DeleteOutlined/>
                        </Button>
                    </span>
                </Tooltip>
            )}
        </div>
    );
};

export default QuickStatusPalette;
